import React from "react";
import {useHistory} from "react-router-dom";
import {useSelector} from "react-redux";
import {Button, Typography} from "@material-ui/core";
import GenericDialog from "../../../../../../../components/dialog/GenericDialog";
import {useActions} from "../../../../../../../actions";
import * as AccountActions from "../../../../../../../actions/account";
import * as ClientActions from "../../../../../../../actions/client";
import * as EstimationActions from "../../../../../../../actions/estimation";
import {RootState} from "../../../../../../../reducers";
import {isDebug, isFunc, sleep} from "../../../../../../../utils/commonUtils";
import {datePickerFormat, getAge} from "../../../../../../../utils/dateUtils";
import {getQueryStringValue, setQueryStringValue} from "../../../../../../../utils/queryString";
import {CallingFromObject, NF_IUL_STRATEGY} from "../../../../../../../constants";


type NFIULOptionProps = {
    iulPopUp: boolean;
    setEstimationLoading: Function;
    setIsIUL?: Function;
    setIulPopUp: Function;
    setReportLoading: Function;
    setRequestId: any;
}

const NFIULOption: React.FC<NFIULOptionProps> = (props) => {
    const {iulPopUp, setIsIUL, setIulPopUp} = props;

    const hist = useHistory();
    const debugMode = isDebug();

    const accountActions = useActions(AccountActions);
    const clientActions = useActions(ClientActions);
    const estimationActions = useActions(EstimationActions);

    const [estimationResponse, setEstimationResponse] = React.useState<any>(null);

    const userAuth = useSelector((state: RootState) => state.auth);
    const verifyInviteData = useSelector((state: RootState) => state.verify);

    const inviteToken = getQueryStringValue("token");

    const user: any = !inviteToken && userAuth ? userAuth.user : verifyInviteData;
    const client = !inviteToken && userAuth ? user.client : verifyInviteData;
    const clientPolicy = !inviteToken && userAuth ? client && client.clientPolicies && client.clientPolicies.length > 0 && client.clientPolicies[0] : verifyInviteData.client;

    const saveContributionAmount = async (payload: any) => {
        try {
            await clientActions.updateClientData(payload, clientPolicy.id, false);

            // Convert client (from kaizen or any) strategy to IUL type
            await clientActions.updateCarrierByUserId({clientPolicyId: clientPolicy.id});
            await accountActions.checkProfileDetailsCompleted("Enrollment");
        } catch (err) {
            return false;
        }
    };

    const saveContributionDetails = async () => {
        if (estimationResponse && estimationResponse.hasOwnProperty("common") && estimationResponse.hasOwnProperty("illustration")) {
            const {common, illustration, reports} = estimationResponse;
            const responseDataIllustrationId = (reports && reports.client && reports.client.illustrationId) || "";
            let issueAge: number = common && common.issueage;
            let gender: number = ((common && common.gender) === "Female" ? 2 : 1);
            let dob = datePickerFormat(common && common.dateofbirth);
            let age = getAge(dob);
            let contributionAmount: number = common && common.contributionamount;
            let employerContributionAmount: number = 0;
            if (common && common.contributionamountemployer) {
                employerContributionAmount = common.contributionamountemployer;
                contributionAmount = contributionAmount - employerContributionAmount;
            }
            let rateclass: string = common && common.rateclass;
            if (clientPolicy && clientPolicy.carrierId === 3 && rateclass === "Elite") {
                rateclass = "Preferred";
            }
            const frequency: string = common && common.paymentfrequency && common.paymentfrequency.toLowerCase();
            const rateClassArray: any = rateclass.split("_");
            let healthType: string = rateClassArray[0];
            let annualPremium: number = 0;
            let faceAmount: number = 0;
            let termRiderAmount: number = 0;

            if (illustration && illustration.data && illustration.data.table && illustration.data.table.length > 0) {
                const illustrationForAllYears = illustration.data.table;
                annualPremium = illustrationForAllYears && illustrationForAllYears[0] && illustrationForAllYears[0].premiumpaid;
            }

            if (
                    illustration &&
                    illustration.nlgrequest &&
                    illustration.nlgrequest.Transaction &&
                    illustration.nlgrequest.Transaction.DeathBenefitAndFunding &&
                    illustration.nlgrequest.Transaction.DeathBenefitAndFunding.DeathBenefit &&
                    illustration.nlgrequest.Transaction.DeathBenefitAndFunding.DeathBenefit.FaceSolve &&
                    illustration.nlgrequest.Transaction.DeathBenefitAndFunding.DeathBenefit.FaceSolve.SolveDetails &&
                    illustration.nlgrequest.Transaction.DeathBenefitAndFunding.DeathBenefit.FaceSolve.SolveDetails.length > 0
            ) {
                const faceAmountForAllYears = illustration.nlgrequest.Transaction.DeathBenefitAndFunding.DeathBenefit.FaceSolve.SolveDetails;
                faceAmount = faceAmountForAllYears && faceAmountForAllYears[0] && faceAmountForAllYears[0].Amount;
                termRiderAmount = faceAmountForAllYears && faceAmountForAllYears[0] && faceAmountForAllYears[0].AdditionalProtectionBenefit;
            }

            const payload: any = {
                issueAge,
                contributionAmount,
                issuedContribution: contributionAmount,
                employerContributionAmount,
                healthType,
                annualPremium,
                faceAmount,
                termRiderAmount,
                gender,
                dob,
                age,
                frequency,
                clientIllustrations: {
                    illustrationId: responseDataIllustrationId,
                    illustrationClientName: common.fullname,
                    illustrationAgentName: common.agent.fullname,
                    totalDeathBenefit: common.totaldeathbenefit,
                    premium: common.premium,
                    bankContributionAmount: common.bankcontributionamount,
                    annualContribution: common.contributionamount,
                    monthlyContribution: illustration.data.monthlytable && illustration.data.monthlytable.length > 0 ? illustration.data.monthlytable[0].mc : 0,
                },
            };

            await saveContributionAmount(payload);
        }
    };

    const getClientEstimationData = async () => {
        if (!clientPolicy) {
            debugMode && console.error("No client policy found.");
            return;
        }

        const userId = user.id;
        const clientPolicyId = clientPolicy.id;

        const estimationDetails: any = await estimationActions.getEstimationDetailsByUserId(userId, CallingFromObject.ENROLLMENT, clientPolicyId);
        if (!estimationDetails || !estimationDetails.id) {
            debugMode && console.error("No estimation details found for userId:", userId, "and clientPolicyId:", clientPolicyId);
            return;
        }

        debugMode && console.log(estimationDetails && estimationDetails.id, "estimationDetails");

        if (!estimationDetails.stressResponse) {
            debugMode && console.error("No stress response found in estimation details for userId:", userId, "and clientPolicyId:", clientPolicyId);
            return;
        }

        const stressResponse: any = JSON.parse(estimationDetails && estimationDetails.stressResponse);

        if (!stressResponse && !stressResponse.success) {
            debugMode && console.error("No success response found in estimation details for userId:", userId, "and clientPolicyId:", clientPolicyId);
            return;
        }

        debugMode && console.log(estimationDetails, "responsePayload");

        const successResponse: any = stressResponse && stressResponse.success;

        debugMode && console.table(successResponse);
        setEstimationResponse(successResponse);
    };

    // confirm enable NfIUL for this client
    const onConfirm = async () => {
        await sleep(50);

        // update to nfIUL strategyId
        await clientActions.updateStrategyId({
            clientPolicyId: clientPolicy && clientPolicy.id,
            // updating strategyId
            strategyId: NF_IUL_STRATEGY,
        });

        // save details to client profile then wait and then auto run IUL estimator
        await saveContributionDetails();

        // close the dialog
        setIulPopUp(false);

        // set the temp isIUL flag to true
        if (setIsIUL && isFunc(setIsIUL)) {
            setIsIUL(true);
        }

        // update the URL to reflect the change
        const curURL = hist.location.pathname + hist.location.search;

        const waitTime = isDebug() ? 3000 : 50;
        await sleep(waitTime);

        // window.location.reload();
        hist.push('/dashboard');
        await sleep(waitTime);

        hist.push(curURL, {isAtlanticNFIUL: true});
        setQueryStringValue('isAtlanticNFIUL', 'true');
    };

    const onClose = () => {
        setIulPopUp(false);
    };

    const content = (
            <>
                <Typography variant="body1">
                    You have selected the Non-Financed IUL option. Please confirm.
                </Typography>
                <Button
                        size="small"
                        variant="contained"
                        className="mt20 mb20 floatRight"
                        color="secondary"
                        onClick={onClose}
                >
                    NO
                </Button>
                <Button
                        size="small"
                        variant="contained"
                        className="mt20 mb20 floatRight mr15"
                        color="primary"
                        onClick={onConfirm}
                >
                    YES
                </Button>
            </>
    );

    React.useEffect(() => {
        (async () => {
            await getClientEstimationData();
        })();
    }, [clientPolicy && clientPolicy.id]);

    return (
            <GenericDialog
                    aria-describedby="nfiul-confirm-dialog"
                    content={content}
                    fullWidth
                    keepMounted
                    maxWidth="sm"
                    onClose={onClose}
                    open={iulPopUp}
                    title="NFIUL OPTION"
            />
    );
};

export default NFIULOption;